import { Address, Alternative, CountryCode } from '@guiker/base-entity'
import { capitalize } from '@guiker/lodash'

import { capitalizeStreetName, extractStreetName, uniqueStrings } from './utils'

type ComponentName = {
  text: string
  languageCode: string
}

type AddressComponent = {
  componentName: ComponentName
  componentType: string
  confirmationLevel: string
}

type Geocode = {
  location: {
    latitude: number
    longitude: number
  }
  placeId: string
}

type AddressValidationResponse = {
  responseId: string
  result: {
    verdict: {
      addressComplete: boolean
      hasInferredComponents?: boolean
      hasUnconfirmedComponents: boolean
    }
    address: {
      formattedAddress: string
      postalAddress: {
        regionCode: string //CLDR region code ie. 'CA', 'US'
        languageCode: string // en
        postalCode: string
        locality: string // city
        addressLines: string[]
        administrativeArea?: string //state, province, ie. switzerland is blank
      }
      addressComponents: AddressComponent[]
    }
    geocode: Geocode
    metadata: {
      business: boolean
      residential: boolean
    }
    uspsData?: {
      // only for US addresses
      standardizedAddress: {
        firstAddressLine: string
        firm: string
        secondAddressLine: string
        urbanization: string
        cityStateZipAddressLine: string
        city: string
        state: string
        zipCode: string
        zipCodeExtension: string
      }
    }
  }
}

type PostalAddress = {
  revision?: number
  regionCode: string //CLDR region code ie. 'CA', 'US'
  languageCode?: string
  postalCode?: string
  sortingCode?: string
  administrativeArea?: string
  locality?: string // city
  sublocality?: string
  addressLines: string[]
  recipients?: string[]
  organization?: string
}

export const findAddressComponent = (res: AddressValidationResponse, fieldName: String): string => {
  return res.result.address.addressComponents.find((comp) => comp.componentType === fieldName).componentName.text
}

const findAlternatives = ({ address, res }: { address: Address; res: AddressValidationResponse }): Alternative => {
  const postalAddress = res.result.address.postalAddress
  const uspsAddress = res.result.uspsData

  return {
    zipcode: uniqueStrings([
      !!uspsAddress ? postalAddress.postalCode.split('-')[0] : postalAddress.postalCode,
      findAddressComponent(res, 'postal_code'),
      address.postalCode,
    ]),
    city: uniqueStrings([
      postalAddress.locality,
      findAddressComponent(res, 'locality'),
      address.city,
      ...(address.cityName ? [address.cityName] : []),
    ]),
    street: uniqueStrings([
      extractStreetName(postalAddress.addressLines[0], address.streetNumber),
      findAddressComponent(res, 'route'),
      address.street,
    ]),
  }
}

export const transformAddressValidationResponseToBaseAddress = ({
  res,
}: {
  res: AddressValidationResponse
}): {
  address: Omit<Address, 'city'>
  alternatives: Alternative
  geocode: Geocode
} => {
  const hasUSPSAddress = !!res.result.uspsData
  const postalAddress = res.result.address.postalAddress
  const uspsAddress = res.result.uspsData
  const streetNumber = findAddressComponent(res, 'street_number')
  const address = {
    cityName: hasUSPSAddress ? capitalize(uspsAddress.standardizedAddress.city) : postalAddress.locality,
    country: hasUSPSAddress ? CountryCode.US : (postalAddress.regionCode as CountryCode),
    state: uspsAddress?.standardizedAddress.state ?? postalAddress.administrativeArea,
    street: hasUSPSAddress
      ? capitalizeStreetName(extractStreetName(uspsAddress.standardizedAddress.firstAddressLine, streetNumber))
      : extractStreetName(postalAddress.addressLines[0], streetNumber),
    streetNumber,
    postalCode: hasUSPSAddress ? uspsAddress.standardizedAddress.zipCode : findAddressComponent(res, 'postal_code'),
  } as Address

  const alternatives = findAlternatives({ address, res })

  const geocode = {
    location: {
      latitude: res.result.geocode.location.latitude,
      longitude: res.result.geocode.location.longitude,
    },
    placeId: res.result.geocode.placeId,
  }

  return {
    address,
    alternatives,
    geocode,
  }
}

export { PostalAddress, AddressValidationResponse, AddressComponent, Geocode }
